/**
 * Edit Session Template - Form for editing existing session templates
 * Supports both platform and club-level session template editing
 * Manages activities within the session
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AdminPageLayout, Button, FormInput, FormTextArea, Section, Card, Grid, Col, Badge, LoadingSpinner } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';
import { Plus, Trash2, GripVertical, AlertTriangle } from 'lucide-react';

const EditSessionTemplate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSuccess, showError } = useToast();

  // Determine context from URL
  const location = useLocation();
  const currentPath = location.pathname;
  const { isPlatformAdmin, isClubAdmin, isBranchAdmin } = useMemo(() => ({
    isPlatformAdmin: currentPath.startsWith('/admin/system-templates'),
    isClubAdmin: currentPath.startsWith('/admin/templates'),
    isBranchAdmin: !!currentPath.match(/^\/branch\/[^/]+\/templates/)
  }), [currentPath]);
  const isCoach = currentPath.startsWith('/coach/templates');

  // Set back path
  let backPath;
  if (isPlatformAdmin) {
    backPath = '/admin/system-templates/sessions';
  } else if (isClubAdmin) {
    backPath = '/admin/templates/sessions';
  } else if (isBranchAdmin) {
    const branchId = currentPath.split('/')[2];
    backPath = `/branch/${branchId}/templates/sessions`;
  } else if (isCoach) {
    backPath = '/coach/templates/sessions';
  } else {
    backPath = '/admin/templates/sessions';
  }

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    total_duration_mins: 60,
    is_active: true
  });
  
  const [activities, setActivities] = useState([]);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(true);

  useEffect(() => {
    loadSessionTemplate();
    loadAvailableActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSessionTemplate = async () => {
    try {
      setLoading(true);
      let data;

      if (isPlatformAdmin) {
        data = await apiService.get(`/api/admin/session-templates/${id}?include_activities=true`);
      } else if (isClubAdmin) {
        const userInfo = await apiService.getCurrentUser();
        const clubId = userInfo.club_id;
        data = await apiService.get(`/api/clubs/${clubId}/session-templates/${id}?include_activities=true`);
      } else if (isBranchAdmin) {
        const branchId = currentPath.split('/')[2];
        data = await apiService.get(`/api/branches/${branchId}/session-templates/${id}?include_activities=true`);
      } else if (isCoach) {
        // Coaches load from branch endpoint using their location_id
        const userInfo = await apiService.getCurrentUser();
        const branchId = userInfo.location_id;
        data = await apiService.get(`/api/branches/${branchId}/session-templates/${id}?include_activities=true`);
      }

      if (data) {
        setFormData({
          name: data.name || '',
          description: data.description || '',
          total_duration_mins: data.total_duration_mins || 60,
          is_active: data.is_active !== undefined ? data.is_active : true
        });

        // Load activities if present
        if (data.activities && Array.isArray(data.activities)) {
          setActivities(data.activities.map(act => ({
            activity_template_id: act.activity_template_id || '',
            duration_mins: act.duration_mins || 15,
            sequence_order: act.sequence_order || 0,
            notes: act.notes || ''
          })));
        }
      }
    } catch (err) {
      showError(`Failed to load session template: ${err.message}`);
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableActivities = async () => {
    try {
      setLoadingActivities(true);
      let data;

      if (isPlatformAdmin) {
        const response = await apiService.get('/api/admin/activity-templates?active_only=true');
        data = response?.templates || [];
      } else if (isClubAdmin) {
        const userInfo = await apiService.getCurrentUser();
        const clubId = userInfo.club_id;
        const response = await apiService.get(`/api/clubs/${clubId}/activity-templates?active_only=true`);
        data = response?.templates || [];
        // For club admins: show only club templates
        data = data.filter(template => template.scope === 'club');
      } else if (isBranchAdmin) {
        const branchId = currentPath.split('/')[2];
        const response = await apiService.get(`/api/branches/${branchId}/activity-templates?active_only=true`);
        data = response?.templates || [];
        // For branch admins: show club templates and branch templates (no platform templates)
        data = data.filter(template => template.scope === 'club' || template.scope === 'branch');
      } else if (isCoach) {
        // Coaches load branch-level activities (same as branch admin)
        const userInfo = await apiService.getCurrentUser();
        const branchId = userInfo.location_id;
        const response = await apiService.get(`/api/branches/${branchId}/activity-templates?active_only=true`);
        data = response?.templates || [];
        // For coaches: show club templates and branch templates
        data = data.filter(template => template.scope === 'club' || template.scope === 'branch');
      }

      setAvailableActivities(data);
    } catch (err) {
      showError(`Failed to load activities: ${err.message}`);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'total_duration_mins' ? parseInt(value, 10) : value)
    }));
  };

  const handleAddActivity = () => {
    setActivities([
      ...activities,
      {
        activity_template_id: '',
        duration_mins: 15,
        sequence_order: activities.length,
        notes: ''
      }
    ]);
  };

  const handleRemoveActivity = (index) => {
    const newActivities = activities.filter((_, i) => i !== index);
    // Resequence
    setActivities(newActivities.map((act, idx) => ({
      ...act,
      sequence_order: idx
    })));
  };

  const handleActivityChange = (index, field, value) => {
    const newActivities = [...activities];
    newActivities[index] = {
      ...newActivities[index],
      [field]: field === 'duration_mins' ? parseInt(value, 10) || 0 : value
    };
    setActivities(newActivities);
  };

  const handleMoveActivity = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === activities.length - 1) return;

    const newActivities = [...activities];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap
    [newActivities[index], newActivities[targetIndex]] = [newActivities[targetIndex], newActivities[index]];
    
    // Resequence
    setActivities(newActivities.map((act, idx) => ({
      ...act,
      sequence_order: idx
    })));
  };

  const getTotalActivitiesDuration = () => {
    return activities.reduce((sum, act) => sum + (act.duration_mins || 0), 0);
  };

  const getDurationMismatch = () => {
    const activitiesDuration = getTotalActivitiesDuration();
    return Math.abs(formData.total_duration_mins - activitiesDuration);
  };

  const hasDurationWarning = () => {
    return getDurationMismatch() > 5;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError('Please provide a session name');
      return;
    }

    if (formData.total_duration_mins < 1 || formData.total_duration_mins > 480) {
      showError('Duration must be between 1 and 480 minutes');
      return;
    }

    if (activities.length === 0) {
      showError('Please add at least one activity to the session');
      return;
    }

    // Validate all activities have templates selected
    for (let i = 0; i < activities.length; i++) {
      if (!activities[i].activity_template_id) {
        showError(`Please select an activity template for activity ${i + 1}`);
        return;
      }
      if (activities[i].duration_mins < 1) {
        showError(`Duration for activity ${i + 1} must be at least 1 minute`);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        total_duration_mins: formData.total_duration_mins,
        is_active: formData.is_active,
        activities: activities
      };

      if (isPlatformAdmin) {
        // Platform admin updating system template
        await apiService.put(`/api/admin/session-templates/${id}`, payload);
        showSuccess('Session template updated successfully');
      } else if (isClubAdmin) {
        // Club admin updating club session template
        const userInfo = await apiService.getCurrentUser();
        const clubId = userInfo.club_id;
        await apiService.put(`/api/clubs/${clubId}/session-templates/${id}`, payload);
        showSuccess('Club session template updated successfully');
      } else if (isBranchAdmin) {
        // Branch admin updating branch session template
        const branchId = currentPath.split('/')[2];
        await apiService.put(`/api/branches/${branchId}/session-templates/${id}`, payload);
        showSuccess('Branch session template updated successfully');
      } else if (isCoach) {
        // Coach updating private branch session template
        const userInfo = await apiService.getCurrentUser();
        const branchId = userInfo.location_id;
        await apiService.put(`/api/branches/${branchId}/session-templates/${id}`, payload);
        showSuccess('Session template updated successfully');
      }

      navigate(backPath);
    } catch (err) {
      showError(`Failed to update session template: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout
        title="EDIT SESSION TEMPLATE"
        subtitle="Loading session template details..."
        backPath={backPath}
        maxWidth="1400px"
      >
        <LoadingSpinner />
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="EDIT SESSION TEMPLATE"
      subtitle={isPlatformAdmin ? "Update platform-level session template" : "Update custom session template"}
      backPath={backPath}
      maxWidth="1400px"
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

          <Section title="Basic Information">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <FormInput
                  label="Session Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Beginner Training Session"
                  required
                />

                <FormTextArea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the purpose and goals of this session..."
                  rows={4}
                />

                <FormInput
                  label="Total Session Duration (minutes) *"
                  name="total_duration_mins"
                  type="number"
                  value={formData.total_duration_mins}
                  onChange={handleChange}
                  min="1"
                  max="480"
                  required
                  help={`Activities total: ${getTotalActivitiesDuration()} minutes`}
                />

                {hasDurationWarning() && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '1rem',
                    background: 'var(--color-warning-bg)',
                    border: '1px solid var(--color-warning)',
                    borderRadius: '8px',
                    color: 'var(--color-warning)'
                  }}>
                    <AlertTriangle size={20} />
                    <span>
                      Session duration ({formData.total_duration_mins} min) differs from activities total ({getTotalActivitiesDuration()} min) by {getDurationMismatch()} minutes
                    </span>
                  </div>
                )}
              </div>
            </Card>
          </Section>

          <Section title="Activities">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {activities.length === 0 ? (
                  <div style={{
                    padding: '3rem',
                    textAlign: 'center',
                    background: 'var(--surface-lvl2)',
                    borderRadius: '12px',
                    border: '2px dashed var(--color-border-light)'
                  }}>
                    <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary)' }}>
                      No activities added yet. Add activities to build your session.
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {activities.map((activity, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          gap: '1rem',
                          padding: '1.5rem',
                          background: 'var(--surface-lvl2)',
                          borderRadius: '12px',
                          border: '1px solid var(--color-border-light)'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => handleMoveActivity(index, 'up')}
                            disabled={index === 0}
                          >
                            ↑
                          </Button>
                          <GripVertical size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => handleMoveActivity(index, 'down')}
                            disabled={index === activities.length - 1}
                          >
                            ↓
                          </Button>
                        </div>

                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Badge variant="outline" size="sm">#{index + 1}</Badge>
                            <strong style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                              Activity
                            </strong>
                          </div>

                          <Grid columns={2} gap="1rem">
                            <Col span={1}>
                              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                                Activity Template *
                              </label>
                              <select
                                value={activity.activity_template_id}
                                onChange={(e) => handleActivityChange(index, 'activity_template_id', e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid var(--color-border)',
                                  borderRadius: '8px',
                                  background: 'var(--surface-lvl1)',
                                  color: 'var(--color-text)'
                                }}
                                required
                              >
                                <option value="">Select Activity</option>
                                {availableActivities.map(act => (
                                  <option key={act.id} value={act.id}>
                                    {act.name} ({act.activity_type}){isBranchAdmin ? ` - ${act.scope === 'club' ? 'Club' : 'Branch'}` : ''}
                                  </option>
                                ))}
                              </select>
                            </Col>

                            <Col span={1}>
                              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                                Duration (minutes) *
                              </label>
                              <input
                                type="number"
                                value={activity.duration_mins}
                                onChange={(e) => handleActivityChange(index, 'duration_mins', e.target.value)}
                                min="1"
                                max="300"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid var(--color-border)',
                                  borderRadius: '8px',
                                  background: 'var(--surface-lvl1)',
                                  color: 'var(--color-text)'
                                }}
                                required
                              />
                            </Col>
                          </Grid>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                              Notes
                            </label>
                            <textarea
                              value={activity.notes}
                              onChange={(e) => handleActivityChange(index, 'notes', e.target.value)}
                              rows={2}
                              placeholder="Optional notes for this activity..."
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--color-border)',
                                borderRadius: '8px',
                                background: 'var(--surface-lvl1)',
                                color: 'var(--color-text)',
                                resize: 'vertical'
                              }}
                            />
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveActivity(index)}
                          style={{ alignSelf: 'flex-start' }}
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddActivity}
                  disabled={loadingActivities || availableActivities.length === 0}
                >
                  <Plus size={18} /> Add Activity
                </Button>

                {availableActivities.length === 0 && !loadingActivities && (
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    No activity templates available. Please create activity templates first.
                  </p>
                )}
              </div>
            </Card>
          </Section>

          <Section title="Status">
            <Card level={1}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  background: 'var(--surface-lvl2)',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border-light)',
                  flex: 1
                }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <label htmlFor="is_active" style={{ cursor: 'pointer', fontWeight: 600 }}>
                    Active
                  </label>
                </div>
                {formData.is_active ? (
                  <Badge variant="success" size="sm">ACTIVE</Badge>
                ) : (
                  <Badge variant="warning" size="sm">INACTIVE</Badge>
                )}
              </div>
            </Card>
          </Section>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            marginTop: '1rem',
            padding: '2rem',
            background: 'var(--surface-lvl1)',
            borderRadius: '16px',
            border: '1px solid var(--color-border-light)'
          }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(backPath)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="save"
              loading={isSubmitting}
              disabled={!formData.name || activities.length === 0}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </form>
    </AdminPageLayout>
  );
};

export default EditSessionTemplate;
